import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutGrid, Building2, FileSpreadsheet, ScrollText, Settings, ChevronsLeft,
  ChevronsRight, Receipt, FileBarChart, Users, LineChart, Wallet, ShieldCheck,
  TrendingDown, Inbox, BarChart3, Percent, HandCoins, Landmark, X, Calculator,
  CalendarDays, Wrench, RefreshCcw,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/data/auth'
import { usePrefs } from '@/data/preferences'

// Preserve existing route visibility and role gates while grouping related work.
const NAV_GROUPS = [
  { label: 'Overview', items: [
    { to: '/dashboard', label: 'Overview', icon: LayoutGrid, end: true },
    { to: '/dashboard/analytics', label: 'Analytics', icon: LineChart },
  ] },
  { label: 'Portfolio', items: [
    { to: '/dashboard/properties', label: 'Properties', icon: Building2 },
    { to: '/dashboard/rent-roll', label: 'Rent Roll', icon: Users },
    { to: '/dashboard/cap-rates', label: 'Cap Rates', icon: Percent },
  ] },
  { label: 'Financials', items: [
    { to: '/dashboard/monthly-pnl', label: 'Monthly P&L', icon: CalendarDays },
    { to: '/dashboard/income-statement', label: 'Income Statement', icon: FileSpreadsheet },
    { to: '/dashboard/cash-flow', label: 'Cash Flow', icon: Wallet },
    { to: '/dashboard/bank-accounts', label: 'Bank Accounts', icon: Landmark },
    { to: '/dashboard/owner-draws', label: 'Owner Draws', icon: HandCoins, adminOnly: true },
    { to: '/dashboard/statements', label: 'Owner Statements', icon: Receipt },
    { to: '/dashboard/top-costs', label: 'Top Costs', icon: TrendingDown },
    { to: '/dashboard/tax', label: 'Tax', icon: Calculator },
  ] },
  { label: 'Operations', items: [
    { to: '/dashboard/maintenance', label: 'Maintenance', icon: Wrench },
    { to: '/dashboard/renewals', label: 'Renewals', icon: RefreshCcw, adminOnly: true },
    { to: '/dashboard/bills', label: 'Bills & Vendors', icon: FileBarChart, adminOnly: true },
    { to: '/dashboard/inquiries', label: 'Inquiries', icon: Inbox, liveBadge: 'newLeads', adminOnly: true },
  ] },
  { label: 'Administration', items: [
    { to: '/dashboard/reports', label: 'Reports', icon: ScrollText },
    { to: '/dashboard/users', label: 'Users', icon: ShieldCheck, adminOnly: true },
    { to: '/dashboard/settings', label: 'Settings', icon: Settings },
    { to: '/dashboard/site-analytics', label: 'Site Analytics', icon: BarChart3, hidden: true },
  ] },
]

export function getRouteContext(pathname) {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((entry) => entry.end
      ? pathname === entry.to || pathname === `${entry.to}/`
      : pathname === entry.to || pathname.startsWith(`${entry.to}/`))
    if (item) return { section: group.label, title: item.label }
  }
  return { section: 'Workspace', title: 'Dashboard' }
}

function useNewLeadsCount(enabled) {
  const [count, setCount] = useState(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    async function pull() {
      try {
        const response = await fetch('/api/leads?status=new&limit=1', { credentials: 'include' })
        if (!response.ok) return
        const data = await response.json()
        if (!cancelled) setCount(data?.counts?.by_status?.new ?? 0)
      } catch { /* Keep navigation usable when the badge cannot refresh. */ }
    }
    pull()
    const interval = setInterval(pull, 60_000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [enabled])
  return count
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const newLeads = useNewLeadsCount(isAdmin)
  const location = useLocation()
  const drawerRef = useRef(null)
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.hidden && (!item.adminOnly || isAdmin)),
  })).filter((group) => group.items.length)

  useEffect(() => { onMobileClose?.() }, [location.pathname, onMobileClose])

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)')
    const handleResize = () => { if (desktop.matches) onMobileClose?.() }
    desktop.addEventListener('change', handleResize)
    return () => desktop.removeEventListener('change', handleResize)
  }, [onMobileClose])

  useEffect(() => {
    if (!mobileOpen) return
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const drawer = drawerRef.current
    const getFocusable = () => Array.from(drawer?.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex="0"]',
    ) || []).filter((element) => element.getClientRects().length > 0)
    getFocusable()[0]?.focus()
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onMobileClose?.()
      }
      if (event.key !== 'Tab') return
      const elements = getFocusable()
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (!first) { event.preventDefault(); drawer?.focus(); return }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [mobileOpen, onMobileClose])

  const renderContent = (isMobile = false) => {
    const isCollapsed = !isMobile && collapsed
    return (
      <>
        <div className={cn('workspace-brand', isCollapsed && 'is-collapsed')}>
          <span className="workspace-icon" aria-hidden="true"><Building2 size={20} strokeWidth={1.6} /></span>
          {!isCollapsed && (
            <div className="workspace-wordmark">
              <span>Pinnacle</span>
              <span>Properties</span>
            </div>
          )}
          {isMobile && (
            <button type="button" onClick={onMobileClose} className="shell-icon-button ml-auto" aria-label="Close navigation">
              <X size={19} />
            </button>
          )}
        </div>
        <nav aria-label="Primary navigation" className={cn('workspace-nav', isCollapsed && 'is-collapsed')}>
          {groups.map((group) => (
            <div key={group.label} className="workspace-nav-group">
              <div className={cn('workspace-nav-label', isCollapsed && 'sr-only')}>{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  aria-label={isCollapsed ? item.label : undefined}
                  title={isCollapsed ? item.label : undefined}
                  className={({ isActive }) => cn('workspace-nav-link', isActive && 'is-active')}
                >
                  <item.icon size={17} strokeWidth={1.65} aria-hidden="true" />
                  {!isCollapsed && <span className="workspace-nav-text">{item.label}</span>}
                  {item.liveBadge === 'newLeads' && newLeads > 0 && !isCollapsed && (
                    <span className="workspace-nav-badge" aria-label={`${newLeads} new inquiries`}>{newLeads}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className={cn('workspace-sidebar-footer', isCollapsed && 'is-collapsed')}>
          <FontSizeControl collapsed={isCollapsed} />
          {!isMobile && (
            <button
              type="button"
              onClick={onToggle}
              className="sidebar-collapse-button"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /><span>Collapse sidebar</span></>}
            </button>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <aside className={cn('workspace-sidebar', collapsed && 'is-collapsed')} aria-label="Workspace navigation">
        {renderContent()}
      </aside>
      {mobileOpen && (
        <div className="workspace-drawer-layer">
          <div onClick={onMobileClose} className="workspace-drawer-backdrop" aria-hidden="true" />
          <aside
            ref={drawerRef}
            id="workspace-mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="Workspace navigation"
            tabIndex={-1}
            className="workspace-drawer"
          >
            {renderContent(true)}
          </aside>
        </div>
      )}
    </>
  )
}

const FONT_SIZES = [
  { id: 'small', title: 'Small text', className: 'text-[11px]' },
  { id: 'medium', title: 'Medium text', className: 'text-[13px]' },
  { id: 'large', title: 'Large text', className: 'text-[15px]' },
]

function FontSizeControl({ collapsed }) {
  const { prefs, update } = usePrefs()
  if (collapsed) {
    const next = () => {
      const index = FONT_SIZES.findIndex((size) => size.id === prefs.fontScale)
      update({ fontScale: FONT_SIZES[(index + 1) % FONT_SIZES.length].id })
    }
    return <button type="button" onClick={next} className="font-size-cycle" title={`Text size: ${prefs.fontScale}. Click to cycle.`} aria-label={`Text size ${prefs.fontScale}; cycle text size`}>Aa</button>
  }
  return (
    <div className="sidebar-font-control" role="group" aria-label="Text size">
      <span>Text size</span>
      {FONT_SIZES.map((size) => (
        <button
          type="button"
          key={size.id}
          onClick={() => update({ fontScale: size.id })}
          title={size.title}
          aria-label={size.title}
          aria-pressed={prefs.fontScale === size.id}
          className={cn(size.className, prefs.fontScale === size.id && 'is-active')}
        >A</button>
      ))}
    </div>
  )
}
